import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { sortByName } from '../../../utils/sort';
import type { Supplier } from '../types';

interface UseSuppliersReturn {
  suppliers: Supplier[];
  addSupplier: (name: string, email: string) => Promise<Supplier | null>;
}

export function useSuppliers(orgId: string | null): UseSuppliersReturn {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  useEffect(() => {
    if (!orgId) {
      setSuppliers([]);
      return;
    }
    const load = async () => {
      try {
        const { data } = await supabase
          .from('suppliers')
          .select('id, name, organization_id, archived, created_at')
          .eq('organization_id', orgId)
          .eq('archived', false)
          .order('name');
        setSuppliers(data ?? []);
      } catch {
        setSuppliers([]);
      }
    };
    load();
  }, [orgId]);

  const addSupplier = async (name: string, email: string): Promise<Supplier | null> => {
    if (!orgId) return null;
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .insert({ name, email: email || null, organization_id: orgId, archived: false })
        .select()
        .single();
      if (error) throw error;
      const created = data as Supplier;
      setSuppliers((prev) => sortByName([...prev, created]));
      return created;
    } catch {
      return null;
    }
  };

  return { suppliers, addSupplier };
}
